import type { Component } from 'vue';
import { Activity, Bike, Dumbbell, Footprints, HeartPulse, Waves } from 'lucide-vue-next';
import type { DiaryDayEntryIntensity } from '@macro-tracker/models';

/**
 * Exercise Types Service
 * Defines system exercise types with MET (Metabolic Equivalent) values
 * and calorie calculation utilities
 */

/** @see {@link DiaryDayEntryIntensity} — canonical definition in \`@macro-tracker/models\` */
export type ExerciseIntensity = DiaryDayEntryIntensity;

export interface ExerciseType {
  id: string;
  name: string;
  icon: Component;
  metValues: {
    low: number;
    medium: number;
    high: number;
  };
}

export const SYSTEM_EXERCISE_TYPES: ExerciseType[] = [
  {
    id: 'running',
    name: 'Running',
    icon: Activity,
    metValues: {
      low: 6.0,
      medium: 8.3,
      high: 11.0,
    },
  },
  {
    id: 'walking',
    name: 'Walking',
    icon: Footprints,
    metValues: {
      low: 2.5,
      medium: 3.5,
      high: 5.0,
    },
  },
  {
    id: 'cycling',
    name: 'Cycling',
    icon: Bike,
    metValues: {
      low: 4.0,
      medium: 6.8,
      high: 10.0,
    },
  },
  {
    id: 'swimming',
    name: 'Swimming',
    icon: Waves,
    metValues: {
      low: 4.5,
      medium: 7.0,
      high: 10.0,
    },
  },
  {
    id: 'weightlifting',
    name: 'Weightlifting',
    icon: Dumbbell,
    metValues: {
      low: 3.5,
      medium: 5.0,
      high: 6.0,
    },
  },
  {
    id: 'cardio',
    name: 'Cardio (General)',
    icon: HeartPulse,
    metValues: {
      low: 4.0,
      medium: 6.5,
      high: 9.0,
    },
  },
];

/**
 * Calculate calories burned during exercise
 * Formula: MET * weight (kg) * (duration (minutes) / 60)
 * @param met - Metabolic Equivalent value
 * @param weightKg - Body weight in kilograms
 * @param durationMinutes - Exercise duration in minutes
 * @returns Calories burned (rounded to nearest integer)
 */
export function calculateExerciseCalories(
  met: number,
  weightKg: number,
  durationMinutes: number,
): number {
  return Math.round(met * weightKg * (durationMinutes / 60));
}

/**
 * Get exercise type by ID
 * @param id - Exercise type ID
 * @returns Exercise type or undefined if not found
 */
export function getExerciseTypeById(id: string): ExerciseType | undefined {
  return SYSTEM_EXERCISE_TYPES.find((type) => type.id === id);
}
